import {GoogleTagManagerService} from 'angular-google-tag-manager';
import {Observable, of} from 'rxjs';


import {ApiResponse, CurrencyTypesEnum, ClientStatusesEnum, HttpStatusCodesEnum} from '@spa/api';

import {Language} from '../../language';
import {RadioGroupOptionInterface} from '../../components/radio-group/interfaces';

import {environment} from '../../../../environments/environment';

import {FilesHelper} from './files.helper';
import {TextsHelper} from './texts.helper';
import {ErrorsHelper} from './errors.helper';
import {EnumsHelper} from './enums.helper';
import {DatesHelper} from './dates.helper';
import {Router} from '@angular/router';
import {PortalPaths} from '../../paths';
import {Injector} from '@angular/core';
import {Product} from "../../../pages/portal/services";

const alaricTemplateHtml = './tenants/alaric.html';
const prototypeTemplateHtml = './tenants/prototype.html';
const alaricTemplateScss = './tenants/alaric.scss';
const prototypeTemplateScss = './tenants/prototype.scss';

export class Helper {
	static errorsHelper = () => ErrorsHelper;
	static filesHelper = () => FilesHelper;
	static textsHelper = () => TextsHelper;
	static enumsHelper = () => EnumsHelper;
	static datesHelper = () => DatesHelper;

	private static injector: Injector;
	private static gtmTagInitialized = false;

	static setInjector(injector: Injector) {
		Helper.injector = injector;
	}

	static initializeGoogleTagManager() {
		if (!Helper.injector) {
			console.error('Check helper class injector');
			return ;
		}

		if (this.gtmTagInitialized) {
			return ;
		}

		const gtmService = Helper.injector.get(GoogleTagManagerService);
		this.gtmTagInitialized = true;

		gtmService.addGtmToDom().finally(() => {
			this.publishGoogleTagCookieEvent();
		});
	}

	static publishGoogleTagEvent(tagName: string) {
		if (!Helper.injector) {
			console.error('Check helper class injector');
			return;
		}

		const gtmService = Helper.injector.get(GoogleTagManagerService);

		const gtmTag = {
			event: tagName,
			accountType: 'Standard',
			authId: environment.authId,
		};

		gtmService.pushTag(gtmTag).finally();
	}

	static publishGoogleTagCookieEvent() {
		if (!Helper.injector) {
			console.error('Check helper class injector');
			return;
		}
		const gtmService = Helper.injector.get(GoogleTagManagerService);
		gtmService.getDataLayer().push("consent", "default", {
			ad_storage: "denied",
			ad_user_data: "denied",
			ad_personalization: "denied",
			analytics_storage: "denied",
			functionality_storage: "denied",
			personalization_storage: "denied",
			security_storage: "granted",
			wait_for_update: 2000,
		});
		gtmService.getDataLayer().push("set", "ads_data_redaction", true);
		gtmService.getDataLayer().push("set", "url_passthrough", true);
	}

	static getEnrollStatuses(): number[] {
		return [
			ClientStatusesEnum.New,
			ClientStatusesEnum.PendingAcceptance,
			ClientStatusesEnum.PendingDocuments
		];
	}

	static compareSimpleArrays<ArrayType>(a: ArrayType[], b: ArrayType[]): boolean {
		return (a.length === b.length) && a.every((item: ArrayType, index: number) => {
			return item === b[index];
		});
	}


	static getDecimalSeparator(language: string): string {
		return language === Language.English ? '.' : ',';
	}

	static getMatchAmountRegex(toFourthDecimal: boolean, language: string): RegExp {
		if (language === Language.Bulgarian) {
			if (!toFourthDecimal) {
				return /^(\d(\s)*)+(,)?(\d{1,2})?$/;
			}

			return /^(\d(\s)*)+(,)?(\d{1,4})?$/;
		} else {
			if (!toFourthDecimal) {
				return /^(\d(,)*)+(\.)?(\d{1,2})?$/;
			}

			return /^(\d(,)*)+(\.)?(\d{1,4})?$/;
		}
	}

	static getFormatAmountRegex(language: string): [RegExp, string] {
		const replace = language === Language.Bulgarian ? '$1 ' : '$1,';
		return [/(\d)(?=(\d{3})+$)/g, replace];
	}

	static createSuccessResponse<DataType>(data: DataType): ApiResponse<DataType> {
		return new ApiResponse<DataType>(HttpStatusCodesEnum.Ok, data);
	}

	static createSuccessResponseObservable<DataType>(data: DataType): Observable<ApiResponse<DataType>> {
		const response = new ApiResponse<DataType>(HttpStatusCodesEnum.Ok, data);

		return of(response);
	}

	static getCurrencyOptionsByCurrentLanguage(currentLanguage: string): RadioGroupOptionInterface[] {

		return [
			{
				label: 'EUR',
				value: 'EUR',
				checked: true
			},
			{
				label: 'USD',
				value: 'USD'
			},
			{
				label: 'BGN',
				value: 'BGN'
			},
			{
				label: 'GBP',
				value: 'GBP'
			}
		];
	}

	static getMainSiteUrl(language: string): string {
		const queryParams = `?lang=${language}&fromfund=1`;

		return `${environment.mainSiteUrl}${queryParams}`;
	}

	static getCurrentPage(router: Router): string {
		let url: string = router.url;
		const queryParamsIndex = router.url.indexOf('?');

		if (queryParamsIndex > 0) {
			url = url.substring(0, queryParamsIndex);
		}

		const urlParts = url.split('/');

		return urlParts[3] ?? '';
	}

	static getEnrollSiteUrl(
		language: string,
		redirectPage: string,
		type = 'enroll',
		fromPage: string,
	): string {
		const queryParams = `${redirectPage}?lang=${language}&type=${type}&frompage=${fromPage}`;

		if (language === Language.English) {
			return `${environment.mainSiteUrl}en/${queryParams}`;
		}

		return `${environment.mainSiteUrl}${queryParams}`;
	}

	static compactString(value: string, language: string): string {
		let result = value.replace(/ /g, '')

		result = language === Language.English
			? result.replace(/,/g, '')
			: result.replace(/,/g, '.');

		return result;
	}

	static getCurrencySign(currency: string): string {
		return currency?.toUpperCase() === CurrencyTypesEnum.Usd ? '$' : '€';
	}

	static isTennAccount(accountReferenceId: string): boolean {
		return accountReferenceId?.startsWith('HWL');
	}

	static isPrototypePortal(): boolean {
		return environment.portal === 'PrototypeFundPortal';
	}

	static getPortalComponentFile(
		componentAlaric: string,
		componentPrototype: string,
	): string {
		return Helper.isPrototypePortal() ? componentPrototype : componentAlaric;
	}

	static getPortalTemplate(): string {
		return environment.componentTemplateFile;
	}

	static getPortalStyle(): string {
		return environment.componentStyleFile;
	}

	static getPortalHomepage(language: string, isAuthenticated: boolean): string {
		const path = Helper.getPortalHomepagePath(isAuthenticated);

		return `/${language}/${PortalPaths.root}/${path}`;
	}

	static getPortalHomepagePath(isAuthenticated: boolean): string {
		return isAuthenticated && Helper.isPrototypePortal()
			? PortalPaths.portfolio.path
			: PortalPaths.dashboard.path;
	}

}
